import React, { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import { StatItem } from "./StatItem";
import { Team } from "../../requests/teams";

type Props = {
  stats: StatItem[];
  homeTeam: Team;
  awayTeam: Team;
};

export const EChartsBarComparison: React.FC<Props> = ({ stats, homeTeam, awayTeam }) => {
  const { categories, homeValues, awayValues, homePct, awayPct } = useMemo(() => {
    const metricNames: string[] = [];
    const homeRaw: number[] = [];
    const awayRaw: number[] = [];
    const homeNorm: number[] = [];
    const awayNorm: number[] = [];

    stats.forEach((s) => {
      const h = Number.parseFloat(s.home) || 0;
      const a = Number.parseFloat(s.away) || 0;
      const total = h + a;
      metricNames.push(s.metric);
      homeRaw.push(h);
      awayRaw.push(a);
      if (total > 0) {
        homeNorm.push((h / total) * 100);
        awayNorm.push((a / total) * 100);
      } else {
        homeNorm.push(50);
        awayNorm.push(50);
      }
    });

    return {
      categories: metricNames,
      homeValues: homeRaw,
      awayValues: awayRaw,
      homePct: homeNorm,
      awayPct: awayNorm,
    };
  }, [stats]);

  const option = useMemo(
    () => ({
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (params: any[]) => {
          if (!params || !params.length) return "";

          const dataIndex = params[0]?.dataIndex ?? 0;
          const metricName = categories[dataIndex] || "Metric";
          let content = `<div><strong>${metricName}</strong></div>`;

          params.forEach((param) => {
            if (param?.data?.rawValue !== undefined) {
              const seriesName = param.seriesName || "";
              const color = param.color || "#999";
              content += `<div style="margin: 2px 0;"><span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${color};"></span>${seriesName}: ${param.data.rawValue}</div>`;
            }
          });

          return content || "No data available";
        },
      },

      legend: { show: false },
      grid: { left: 12, right: 12, top: 44, bottom: 28, containLabel: true },
      xAxis: {
        type: "value",
        min: 0,
        max: 100,
        splitLine: { show: false },
        axisLabel: { show: false },
      },
      yAxis: {
        type: "category",
        data: categories,
        axisTick: { show: false },
        axisLine: { show: false },
        axisLabel: { show: false },
      },
      series: [
        {
          name: homeTeam.name,
          type: "bar",
          stack: "total",
          data: homePct.map((p, i) => ({ value: p, rawValue: homeValues[i] })),
          itemStyle: {
            color: homeTeam.color,
            borderColor: "#e0e0e0",
            borderWidth: 1,
            borderRadius: [8, 0, 0, 8],
          },
          label: {
            show: true,
            position: "inside",
            formatter: (params: any) => `${params.data.rawValue}`,
            fontWeight: 700,
            fontSize: 16,
          },
          barCategoryGap: "50%",
        },
        {
          name: awayTeam.name,
          type: "bar",
          stack: "total",
          data: awayPct.map((p, i) => ({ value: p, rawValue: awayValues[i] })),
          itemStyle: {
            color: awayTeam.color,
            borderColor: "#e0e0e0",
            borderWidth: 1,
            borderRadius: [0, 8, 8, 0],
          },
          label: {
            show: true,
            position: "inside",
            formatter: (params: any) => `${params.data.rawValue}`,
            fontWeight: 700,
            fontSize: 16,
          },
          barCategoryGap: "50%",
        },
        // Centered metric labels overlay
        {
          name: "labels",
          type: "scatter",
          data: categories.map((c) => [50, c]),
          symbolSize: 0,
          z: 20,
          tooltip: { show: false },
          label: {
            show: true,
            formatter: (params: any) => categories[params.dataIndex],
            position: "top",
            align: "center",
            verticalAlign: "bottom",
            offset: [0, -18],
            color: "#555",
            fontWeight: 600,
            fontSize: 18,
          },
        },
      ],
    }),
    [categories, homePct, awayPct, homeTeam, awayTeam, homeValues, awayValues]
  );

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <ReactECharts option={option} style={{ width: "100%", height: "100%" }} notMerge lazyUpdate />
    </div>
  );
};
